// src/components/icons/Logo.tsx
"use client";

import React from "react";

interface LogoProps {
  size?: number;
  className?: string;
  ariaLabel?: string;
}

/**
 * Logo oficial de la aplicación - Logo10 (Preset Knob Clean)
 *
 * Versión minimalista de un knob de amplificador con:
 * - Fondo con gradiente púrpura/rosa
 * - Arco de progreso de 270°
 * - Indicador apuntando a -45° (1:30 en reloj)
 * - Textura radial en knob central
 *
 * @param size - Tamaño del logo en píxeles (default: 48)
 * @param className - Clases CSS adicionales
 */
const Logo = ({ size = 48, className = "", ariaLabel = "RigCraft logo" }: LogoProps) => {
  // CB-01: IDs únicos para gradientes evitan conflictos cuando se renderizan múltiples instancias
  const gradId = React.useId();
  const gradRingId = React.useId();
  const knobTextureId = React.useId();

  // Cálculo del indicador: ángulo -45° (apunta a las 1:30 en reloj)
  const indicatorAngleSVG = -45;
  const radians = indicatorAngleSVG * (Math.PI / 180);
  const indicatorX = 24 + 8 * Math.cos(radians);
  const indicatorY = 24 + 8 * Math.sin(radians);

  // Longitud del arco: 50% del recorrido total de 270°
  const arcLength = 44;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      width={size}
      height={size}
      className={className}
      aria-label={ariaLabel}
    >
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8050e8" />
          <stop offset="100%" stopColor="#ec4899" />
        </linearGradient>
        <linearGradient id={gradRingId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#a855f7" />
          <stop offset="50%" stopColor="#ec4899" />
          <stop offset="100%" stopColor="#8050e8" />
        </linearGradient>
        <radialGradient id={knobTextureId} cx="30%" cy="30%">
          <stop offset="0%" stopColor="#3a3a3a" />
          <stop offset="100%" stopColor="#1a1a1a" />
        </radialGradient>
      </defs>
      {/* Fondo con gradiente */}
      <rect x="2" y="2" width="44" height="44" rx="12" fill={`url(#${gradId})`} />
      {/* Base del knob */}
      <circle cx="24" cy="24" r="18" fill="#0f0f0f" />
      {/* Arco de fondo (track completo 270°) */}
      <circle
        cx="24"
        cy="24"
        r="14"
        fill="none"
        stroke="#333"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray="66 22"
        transform="rotate(135 24 24)"
      />
      {/* Arco de valor */}
      <circle
        cx="24"
        cy="24"
        r="14"
        fill="none"
        stroke={`url(#${gradRingId})`}
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={`${arcLength} ${88 - arcLength}`}
        transform="rotate(135 24 24)"
      />
      {/* Knob central con textura */}
      <circle cx="24" cy="24" r="10" fill={`url(#${knobTextureId})`} />
      {/* Borde metálico del knob */}
      <circle cx="24" cy="24" r="10" fill="none" stroke="#444" strokeWidth="1" />
      {/* Indicador */}
      <line
        x1="24"
        y1="24"
        x2={indicatorX}
        y2={indicatorY}
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
      />
      {/* Punto central */}
      <circle cx="24" cy="24" r="2" fill="#666" />
    </svg>
  );
};

export default Logo;
